import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SparkMD5 from "spark-md5";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import {
  curatorArtworkSchema,
  curatorDashboardSchema,
  curatorGovernanceSchema,
  exhibitionWorkspaceSchema,
  uploadIntentSchema,
  type CuratorArtwork,
  type ExhibitionWorkspace
} from "@eaf/contracts";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MutationNotice,
  PageFrame,
  ProgressiveSection,
  ResponsiveTable,
  WorkflowStatus
} from "@eaf/ui";
import { useAuth } from "../auth/auth-context";
import { Seo } from "../components/seo";
import { TitleSuggestions } from "../components/title-suggestions";
import { StorytellingEditor } from "../components/storytelling-editor";
import { readSessionDraft, writeSessionDraft } from "../lib/session-draft";
import { useLiveHistory, liveHistoriesSchema } from "../lib/live-history";
import { LiveHistoryControls } from "../components/live-history-controls";
import { informationOptions, mediumValues } from "../components/artwork-information-fields";
import { formatMachineLabel } from "../lib/curator-labels";

const draftFormSchema = z.record(
  z.string(),
  z.union([z.string().max(20_000), z.array(z.string().max(20_000))])
);
type DraftForm = z.infer<typeof draftFormSchema>;

const fieldGroups = [
  {
    title: "Artwork Details",
    initiallyOpen: true,
    fields: [
      ["title", "Title"],
      ["recordId", "ID#"],
      ["sequence", "Sequence"],
      ["dateDisplay", "Created"],
      ["artCondition", "Condition"],
      ["conditionDetails", "Condition Details", "textarea"],
      ["support", "Paper"],
      ["pressType", "Press Type"],
      ["texture", "Texture"],
      ["tint", "Tint"],
      ["materials", "Medium"],
      ["orientation", "Orientation"],
      ["ratio", "Ratio"],
      ["width", "Width"],
      ["height", "Height"],
      ["publish", "Publish"],
      ["sale", "Sale"],
      ["sold", "Sold"],
      ["storage", "Storage"]
    ]
  }
] as const;

export function CuratorGate() {
  const auth = useAuth();
  if (auth.state === "loading")
    return (
      <PageFrame>
        <LoadingState label="Verifying curator access" />
      </PageFrame>
    );
  if (auth.state === "anonymous") return <CuratorLogin />;
  if (auth.state === "forbidden" || auth.state === "error")
    return (
      <PageFrame narrow>
        <ErrorState title="Curator Workspace Unavailable">
          {auth.message ?? "This account does not have EAF access."}
        </ErrorState>
      </PageFrame>
    );
  if (
    !auth.currentUser?.permissions.some((permission) =>
      ["artwork.create", "artwork.update", "catalog.read_private"].includes(permission)
    )
  )
    return (
      <PageFrame narrow>
        <ErrorState title="Curator Permission Is Not Assigned">
          Your identity is valid, but it does not have curator workspace permission.
        </ErrorState>
      </PageFrame>
    );
  return <Outlet />;
}

function CuratorLogin() {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const data = new FormData(event.currentTarget);
    try {
      await auth.signIn(text(data, "email"), text(data, "password"));
    } catch {
      setError("Sign-in failed. Check the account and password, then try again.");
      setBusy(false);
    }
  };
  return (
    <PageFrame narrow>
      <Seo title="EAF Curator Sign In" description="Authorized EAF curator workspace." />
      <Card className="auth-card">
        <Card.Body>
          <p className="eyebrow">Curator workspace</p>
          <h1>Sign In</h1>
          <p>
            Use your dedicated EAF Firebase identity. EAF verifies curator permissions separately.
          </p>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          <Form onSubmit={submit}>
            <Form.Group className="mb-3" controlId="curator-email">
              <Form.Label>Email</Form.Label>
              <Form.Control name="email" type="email" autoComplete="username" required />
            </Form.Group>
            <Form.Group className="mb-4" controlId="curator-password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                name="password"
                type="password"
                autoComplete="current-password"
                required
              />
            </Form.Group>
            <Button type="submit" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </PageFrame>
  );
}

export function CuratorDashboardPage() {
  const auth = useAuth();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["curator-dashboard"],
    queryFn: () =>
      auth
        .authorizedFetch<unknown>("/curator/dashboard")
        .then((value) => curatorDashboardSchema.parse(value)),
    refetchInterval: 15_000
  });
  const taskMutation = useMutation({
    mutationFn: ({ taskId, command }: { taskId: string; command: "retry" | "cancel" }) =>
      commandRequest(auth, `/curator/tasks/${taskId}/${command}`, "POST", {}),
    onSuccess: () => client.invalidateQueries({ queryKey: ["curator-dashboard"] })
  });
  if (query.isLoading)
    return (
      <PageFrame>
        <LoadingState label="Loading curator work" />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame>
        <ErrorState retry={() => void query.refetch()}>
          The curator work queue is unavailable.
        </ErrorState>
      </PageFrame>
    );
  return (
    <PageFrame>
      <Seo title="Curator Workspace | EAF" description="EAF artwork management." />
      <CuratorHeading>
        <div className="d-flex flex-wrap gap-2">
          <Link className="btn btn-outline-light" to="/curator/exhibitions">
            Exhibition drafts
          </Link>
          <Link className="btn btn-primary" to="/curator/add">
            Add artwork
          </Link>
        </div>
      </CuratorHeading>
      <div className="curator-metrics" aria-label="Work queue summary">
        <Metric label="Continue work" value={query.data.counts.drafts} />
        <Metric label="Needs attention" value={query.data.counts.failed} />
      </div>
      <section className="curator-block">
        <h2>Continue Artwork Work</h2>
        {query.data.drafts.length ? (
          <ResponsiveTable
            caption="Artwork work queue"
            headers={["Sequence", "Artwork", "Stage", "Catalog", "Updated", "Open"]}
            rows={query.data.drafts.map((draft) => [
              draft.photographicSequence ?? "—",
              draft.title || "Untitled draft",
              <WorkflowStatus label={formatMachineLabel(draft.workflowState)} tone="active" />,
              formatMachineLabel(draft.catalogMaturity),
              formatDate(draft.updatedAt),
              <Link to={`/curator/artwork/${draft.artworkId}`}>Continue</Link>
            ])}
          />
        ) : (
          <EmptyState title="No Drafts Are Waiting">
            <p>Begin with one representative front image and its photographic sequence.</p>
          </EmptyState>
        )}
      </section>
      <section className="curator-block">
        <h2>Problems That Need Attention</h2>
        {query.data.processing.length ? (
          <ResponsiveTable
            caption="Processing and recovery"
            headers={["Task", "State", "Updated", "Message", "Action"]}
            rows={query.data.processing.map((task) => [
              formatMachineLabel(task.taskType),
              <WorkflowStatus
                label={formatMachineLabel(task.state)}
                tone={
                  ["failed", "dead_lettered"].includes(task.state)
                    ? "warning"
                    : task.state === "running"
                      ? "active"
                      : "neutral"
                }
              />,
              formatDate(task.updatedAt),
              task.terminalReason ?? "—",
              <div className="d-flex gap-2">
                {["failed", "dead_lettered"].includes(task.state) ? (
                  <Button
                    size="sm"
                    variant="outline-light"
                    onClick={() => taskMutation.mutate({ taskId: task.taskId, command: "retry" })}
                  >
                    Retry
                  </Button>
                ) : null}
                {["pending", "running", "failed"].includes(task.state) ? (
                  <Button
                    size="sm"
                    variant="outline-warning"
                    onClick={() => taskMutation.mutate({ taskId: task.taskId, command: "cancel" })}
                  >
                    Cancel
                  </Button>
                ) : null}
              </div>
            ])}
          />
        ) : (
          <p className="text-secondary">Nothing needs attention right now.</p>
        )}
      </section>
      <p>
        <Link to="/curator/governance">Technical AI and vocabulary details</Link>
      </p>
    </PageFrame>
  );
}

export function AddArtworkPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [sequence, setSequence] = useState("");
  const [reason, setReason] = useState("");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [savedDraftArtworkId, setSavedDraftArtworkId] = useState<string | null>(null);
  const detected = useMemo(() => (file ? leadingSequence(file.name) : null), [file]);
  useEffect(() => {
    setSequence(detected ? String(detected) : "");
  }, [detected]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file || !sequence) return;
    setError(null);
    setSavedDraftArtworkId(null);
    let draftArtworkId: string | null = null;
    try {
      setProgress(10);
      const draft = z.object({ artworkId: z.string().uuid(), version: z.number().int() }).parse(
        await commandRequest(auth, "/curator/artworks", "POST", {
          originalFilename: file.name,
          photographicSequence: Number(sequence),
          ...(Number(sequence) !== detected || !detected
            ? { sequenceCorrectionReason: reason }
            : {})
        })
      );
      draftArtworkId = draft.artworkId;
      await uploadArtworkFile(auth, draft.artworkId, file, setProgress);
      void navigate(`/curator/artwork/${draft.artworkId}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Artwork intake could not be completed.");
      setSavedDraftArtworkId(draftArtworkId);
      setProgress(0);
    }
  };
  return (
    <PageFrame narrow>
      <Seo title="Add Artwork | EAF Curator" description="Begin an EAF artwork record." />
      <Link to="/curator">← Work queue</Link>
      <div className="mt-4 mb-4">
        <p className="eyebrow">Small first step</p>
        <h1>Add Artwork</h1>
        <p>
          Choose one representative front image. Nothing is published or offered for sale during
          intake.
        </p>
      </div>
      {error ? (
        <Alert variant="danger">
          {error}
          {savedDraftArtworkId ? (
            <div className="mt-2">
              <Link to={`/curator/artwork/${savedDraftArtworkId}`}>
                Open the saved draft and retry its image
              </Link>
            </div>
          ) : null}
        </Alert>
      ) : null}
      <Form onSubmit={submit}>
        <Form.Group className="mb-4" controlId="curator-front-image">
          <Form.Label>Representative front image</Form.Label>
          <Form.Control
            type="file"
            accept="image/jpeg,image/png,image/webp"
            required
            onChange={(event) => {
              const input = event.currentTarget as HTMLInputElement;
              setFile(input.files?.item(0) ?? null);
            }}
          />
          <Form.Text>JPEG, PNG, or WebP; no more than 40 MB.</Form.Text>
        </Form.Group>
        {file ? (
          <Card className="curator-card mb-4">
            <Card.Body>
              <strong>{file.name}</strong>
              <p className="mb-2">
                {detected
                  ? `Detected photographic sequence: ${detected}`
                  : "No leading photographic sequence was detected."}
              </p>
              <Form.Group controlId="curator-photographic-sequence">
                <Form.Label>Confirm photographic sequence</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={sequence}
                  onChange={(event) => setSequence(event.currentTarget.value)}
                  required
                />
                <Form.Text>
                  This records photography/upload order. It is not catalog or publication order.
                </Form.Text>
              </Form.Group>
              {Number(sequence) !== detected || !detected ? (
                <Form.Group className="mt-3" controlId="curator-sequence-reason">
                  <Form.Label>Reason for manual entry or correction</Form.Label>
                  <Form.Control
                    value={reason}
                    onChange={(event) => setReason(event.currentTarget.value)}
                    minLength={8}
                    required
                  />
                </Form.Group>
              ) : null}
            </Card.Body>
          </Card>
        ) : null}
        {progress ? (
          <ProgressBar className="mb-3" now={progress} label={`${progress}%`} aria-live="polite" />
        ) : null}
        <Button type="submit" disabled={!file || !sequence || progress > 0}>
          Create draft and upload
        </Button>
      </Form>
    </PageFrame>
  );
}

export function CuratorArtworkPage() {
  const auth = useAuth();
  const { id = "" } = useParams();
  const [draftPreview, setDraftPreview] = useState<{
    artworkId: string;
    title: string;
    dirty: boolean;
  } | null>(null);
  const query = useQuery({
    queryKey: ["curator-artwork", id],
    queryFn: () =>
      auth
        .authorizedFetch<unknown>(`/curator/artworks/${id}`)
        .then((value) => curatorArtworkSchema.parse(value)),
    enabled: Boolean(id)
  });
  if (query.isLoading)
    return (
      <PageFrame>
        <LoadingState label="Loading curator record" />
      </PageFrame>
    );
  if (!query.data)
    return (
      <PageFrame>
        <ErrorState retry={() => void query.refetch()}>
          The curator record is unavailable.
        </ErrorState>
      </PageFrame>
    );
  const record = query.data;
  const title =
    (draftPreview?.artworkId === record.artworkId
      ? draftPreview.title
      : stringValue(record.document.title)) || "Untitled draft";
  const presentationAsset =
    record.assets.find((asset) => asset.role === "web_derivative:xl-webp" && asset.url) ??
    record.assets.find((asset) => asset.role === "web_derivative:lg-webp" && asset.url) ??
    record.assets.find((asset) => asset.kind === "web_derivative" && asset.url);
  return (
    <PageFrame>
      <Seo title={`${title} | EAF Curator`} description="EAF curator artwork workspace." />
      <Link to="/curator">← Work queue</Link>
      {query.isError && (
        <Alert variant="warning">
          The latest record could not be loaded. Your edits are still here.{" "}
          <Button variant="link" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Alert>
      )}
      <div className="curator-heading mt-4">
        <div>
          <p className="eyebrow">Curator artwork workspace</p>
          <h1>{title}</h1>
          {draftPreview?.artworkId === record.artworkId && draftPreview.dirty ? (
            <Badge bg="warning" text="dark">
              Unsaved changes
            </Badge>
          ) : null}
          <p>
            Sequence {record.photographicSequence ?? "—"} · Version {record.version} ·{" "}
            <WorkflowStatus label={formatMachineLabel(record.workflowState)} tone="active" />
          </p>
        </div>
      </div>
      <nav className="curator-local-nav" aria-label="Artwork workspace sections">
        <a href="#record">Artwork information</a>
        <a href="#ai-review">AI Assistant</a>
      </nav>
      <section id="record" className="curator-block">
        <h2>Artwork Information</h2>
        <DraftEditor
          key={`${auth.currentUser?.userId}:${record.artworkId}`}
          record={record}
          onDraftChange={setDraftPreview}
          {...(presentationAsset?.url ? { previewUrl: presentationAsset.url } : {})}
        />
      </section>
      {!record.assets.length ? <ArtworkImageRecovery record={record} /> : null}
      <section id="ai-review" className="curator-block">
        <div className="d-flex flex-wrap justify-content-between gap-3">
          <div>
            <h2>AI Assistant</h2>
          </div>
        </div>
        <StorytellingEditor
          key={`${auth.currentUser?.userId}:${record.artworkId}`}
          record={record}
        />
      </section>
    </PageFrame>
  );
}

function ArtworkImageRecovery({ record }: { record: CuratorArtwork }) {
  const auth = useAuth();
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) return;
    const detected = leadingSequence(file.name);
    if (detected !== record.photographicSequence) {
      setError(
        `Choose a file beginning with sequence ${record.photographicSequence ?? "shown for this draft"}.`
      );
      return;
    }
    setError(null);
    try {
      await uploadArtworkFile(auth, record.artworkId, file, setProgress);
      await client.invalidateQueries({ queryKey: ["curator-artwork", record.artworkId] });
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "The image retry could not be completed."
      );
      setProgress(0);
    }
  };
  return (
    <Alert variant="warning">
      <Alert.Heading>This draft still needs its front image</Alert.Heading>
      <p>
        The draft is safe. Choose the same sequence-numbered file to retry a new private upload.
      </p>
      {error ? <p className="text-danger-emphasis">{error}</p> : null}
      <Form onSubmit={(event) => void submit(event)}>
        <Form.Group controlId={`curator-retry-image-${record.artworkId}`}>
          <Form.Label>Representative front image</Form.Label>
          <Form.Control
            type="file"
            accept="image/jpeg,image/png,image/webp"
            required
            onChange={(event) => {
              const input = event.currentTarget as HTMLInputElement;
              setFile(input.files?.item(0) ?? null);
            }}
          />
          <Form.Text>
            The filename must begin with sequence {record.photographicSequence ?? "for this draft"}.
          </Form.Text>
        </Form.Group>
        {progress ? (
          <ProgressBar className="mt-3" now={progress} label={`${progress}%`} aria-live="polite" />
        ) : null}
        <Button className="mt-3" type="submit" disabled={!file || progress > 0}>
          Retry image upload
        </Button>
      </Form>
    </Alert>
  );
}

export function DraftEditor({
  record,
  previewUrl,
  onDraftChange
}: {
  record: CuratorArtwork;
  previewUrl?: string;
  onDraftChange?: (draft: { artworkId: string; title: string; dirty: boolean }) => void;
}) {
  const auth = useAuth();
  const client = useQueryClient();
  const defaults = useMemo(
    () => ({
      ...Object.fromEntries(
        fieldGroups.flatMap((group) =>
          group.fields.map(([key]) => [key, stringValue(record.document[key])])
        )
      ),
      materials: mediumValues(record.document.materials),
      title: stringValue(record.document.title),
      recordId: record.legacyCatalogId ?? record.artworkId,
      sequence: String(record.photographicSequence ?? ""),
      publish: String(record.publicationState === "published"),
      sale: String(record.commerceState === "available"),
      sold: String(record.commerceState === "sold")
    }),
    [record]
  );
  const form = useForm<DraftForm>({
    resolver: zodResolver(draftFormSchema),
    defaultValues: defaults
  });
  const baselineVersion = useRef(record.version);
  const recoveryKey = `eaf:artwork-draft:${auth.currentUser?.userId}:${record.artworkId}`;
  const [recovery] = useState(() =>
    readSessionDraft(
      recoveryKey,
      z.object({
        version: z.number().int(),
        patch: draftFormSchema,
        baseline: draftFormSchema.optional(),
        histories: liveHistoriesSchema.optional()
      })
    )
  );
  const baselineValues = useRef<DraftForm | null>(
    recovery
      ? (recovery.baseline ?? (recovery.version === record.version ? defaults : null))
      : defaults
  );
  const titleHistory = useLiveHistory(
    {
      title:
        typeof recovery?.patch.title === "string"
          ? recovery.patch.title
          : stringValue(defaults.title)
    },
    recovery?.histories
  );
  const [recoveryAvailable, setRecoveryAvailable] = useState(true);
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const draft = recovery;
    if (draft) {
      for (const [key, value] of Object.entries(draft.patch)) {
        if (JSON.stringify(value) !== JSON.stringify(form.getValues(key))) {
          form.setValue(key, value, { shouldDirty: true });
          baselineVersion.current = draft.version;
        }
      }
    }
  }, [recovery, form]);
  const titleValue = form.watch("title");
  const dirty = form.formState.isDirty;
  const dirtyFields = form.formState.dirtyFields;
  useEffect(() => {
    const persist = () => {
      const patch = Object.fromEntries(
        Object.entries(form.getValues()).filter(([key]) => form.getFieldState(key).isDirty)
      );
      if (
        !writeSessionDraft(recoveryKey, {
          version: baselineVersion.current,
          patch,
          baseline: baselineValues.current ?? undefined,
          histories: titleHistory.histories
        })
      )
        setRecoveryAvailable(false);
    };
    persist();
    const subscription = form.watch(persist);
    return () => subscription.unsubscribe();
  }, [form, recoveryKey, dirtyFields, titleHistory.histories]);
  useEffect(() => {
    onDraftChange?.({
      artworkId: record.artworkId,
      title: typeof titleValue === "string" ? titleValue : "",
      dirty
    });
  }, [onDraftChange, record.artworkId, titleValue, dirty]);
  useEffect(() => {
    if (record.version < baselineVersion.current) return;
    const edits = Object.fromEntries(
      Object.entries(form.getValues()).filter(([key]) => form.getFieldState(key).isDirty)
    );
    // Rebase only when every edited field is unchanged on the server (or already
    // equals this draft). Unrelated saves must not strand the form on an old version.
    if (
      Object.entries(edits).some(
        ([key, value]) =>
          !baselineValues.current ||
          (JSON.stringify(defaults[key as keyof typeof defaults]) !==
            JSON.stringify(baselineValues.current[key]) &&
            JSON.stringify(defaults[key as keyof typeof defaults]) !== JSON.stringify(value))
      )
    )
      return;
    baselineVersion.current = record.version;
    baselineValues.current = defaults;
    form.reset(defaults);
    for (const [key, value] of Object.entries(edits))
      form.setValue(key, value, { shouldDirty: true });
    if (
      !Object.hasOwn(edits, "title") &&
      titleHistory.valuesRef.current.title !== stringValue(defaults.title)
    )
      titleHistory.reset({ title: stringValue(defaults.title) });
  }, [defaults, record.version, form]);
  const mutation = useMutation({
    mutationFn: (values: DraftForm) => {
      const { publish, sale, sold } = values;
      const patch = Object.fromEntries(
        Object.entries(values).filter(
          ([key]) => !["publish", "sale", "sold", "recordId", "sequence"].includes(key)
        )
      );
      return commandRequest(auth, `/curator/artworks/${record.artworkId}`, "PATCH", {
        expectedVersion: baselineVersion.current,
        patch,
        catalogFlags: {
          ...(publish !== undefined ? { publish: publish === "true" } : {}),
          ...(sale !== undefined ? { sale: sale === "true" } : {}),
          ...(sold !== undefined ? { sold: sold === "true" } : {})
        }
      });
    },
    onSuccess: (result, changed) => {
      baselineVersion.current = z.object({ version: z.number().int() }).parse(result).version;
      baselineValues.current = form.getValues();
      form.reset(form.getValues());
      if (typeof changed.title === "string") titleHistory.reset({ title: changed.title });
      return client.invalidateQueries({ queryKey: ["curator-artwork", record.artworkId] });
    }
  });
  const save = () => {
    if (!form.formState.isDirty || mutation.isPending) return;
    void form.handleSubmit((values) =>
      mutation.mutate(
        Object.fromEntries(
          Object.entries(values).filter(([key]) => form.formState.dirtyFields[key])
        )
      )
    )();
  };
  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      {!recoveryAvailable && (
        <Alert variant="warning">
          This browser could not keep a recovery draft. Save your edits before leaving this page.
        </Alert>
      )}
      <MutationNotice
        state={
          mutation.isPending
            ? "working"
            : mutation.isError
              ? "error"
              : mutation.isSuccess
                ? "success"
                : "idle"
        }
        {...(mutation.isError ? { message: errorMessage(mutation.error) } : {})}
      />
      {dirty ? (
        <p role="status" className="text-warning">
          Unsaved changes
        </p>
      ) : null}
      {fieldGroups.map((group) => (
        <ProgressiveSection
          key={group.title}
          title={group.title}
          initiallyOpen={"initiallyOpen" in group ? group.initiallyOpen : false}
        >
          <Row className="g-4 align-items-start">
            <Col lg={4}>
              {previewUrl ? (
                <figure className="curator-artwork-preview">
                  <img
                    src={previewUrl}
                    alt={`${typeof titleValue === "string" && titleValue ? titleValue : "Untitled draft"} representative front view`}
                  />
                </figure>
              ) : (
                <p className="text-secondary">Artwork preview is unavailable.</p>
              )}
            </Col>
            <Col lg={8}>
              <Row className="g-3">
                {group.fields.map(([key, label, control]) => (
                  <Col md={control === "textarea" || key === "title" ? 12 : 6} key={key}>
                    <Form.Group controlId={`curator-field-${key}`}>
                      <div className="curator-field-heading">
                        <Form.Label>{label}</Form.Label>
                        {key === "title" ? (
                          <TitleSuggestions
                            artworkId={record.artworkId}
                            {...(previewUrl ? { previewUrl } : {})}
                            previewTitle={typeof titleValue === "string" ? titleValue : "Artwork"}
                            disabled={mutation.isPending}
                            onUse={(title) => {
                              titleHistory.change("title", title);
                              form.setValue("title", title, { shouldDirty: true });
                            }}
                          />
                        ) : null}
                      </div>
                      {informationOptions[key] ? (
                        <Form.Select
                          {...form.register(key)}
                          multiple={key === "materials"}
                          disabled={
                            mutation.isPending ||
                            (key === "publish" &&
                              !auth.currentUser?.permissions.includes("artwork.publish")) ||
                            (["sale", "sold"].includes(key) &&
                              !auth.currentUser?.permissions.includes("commerce_state.update"))
                          }
                          onChange={(event) => {
                            void form.register(key).onChange(event);
                            if ((key === "sale" || key === "sold") && event.target.value === "true")
                              form.setValue(key === "sale" ? "sold" : "sale", "false", {
                                shouldDirty: true
                              });
                          }}
                        >
                          {key !== "materials" && !["publish", "sale", "sold"].includes(key) ? (
                            <option value="">Choose One</option>
                          ) : null}
                          {[
                            ...new Set([
                              ...informationOptions[key],
                              ...(key === "materials"
                                ? mediumValues(defaults.materials)
                                : [stringValue(record.document[key])]
                              ).filter(Boolean)
                            ])
                          ].map((value) => (
                            <option key={value} value={value}>
                              {value === "true" ? "True" : value === "false" ? "False" : value}
                            </option>
                          ))}
                        </Form.Select>
                      ) : control === "textarea" ? (
                        <Form.Control
                          {...form.register(key)}
                          disabled={mutation.isPending}
                          as="textarea"
                          rows={key === "conditionDetails" ? 10 : 4}
                        />
                      ) : (
                        <Form.Control
                          {...form.register(key)}
                          readOnly={key === "recordId" || key === "sequence"}
                          disabled={mutation.isPending}
                          onChange={(event) => {
                            void form.register(key).onChange(event);
                            if (key === "title")
                              titleHistory.change("title", event.target.value, true);
                          }}
                          onBlur={(event) => {
                            void form.register(key).onBlur(event);
                            if (key === "title") titleHistory.finish("title");
                          }}
                        />
                      )}
                      {key === "title" && (
                        <div className="mt-2">
                          <LiveHistoryControls
                            label="Title"
                            history={titleHistory.histories.title}
                            disabled={mutation.isPending}
                            onMove={(step) =>
                              form.setValue("title", titleHistory.move("title", step), {
                                shouldDirty: true
                              })
                            }
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </ProgressiveSection>
      ))}
      <Button type="button" onClick={save} disabled={!form.formState.isDirty || mutation.isPending}>
        Save changes
      </Button>
    </Form>
  );
}

export function CuratorExhibitionsPage() {
  const auth = useAuth();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["curator-exhibitions"],
    queryFn: () =>
      auth
        .authorizedFetch<unknown>("/curator/exhibitions")
        .then((value) => exhibitionWorkspaceSchema.parse(value))
  });
  const create = useMutation({
    mutationFn: (input: { title: string; purpose: string; curatorNotes: string }) =>
      commandRequest(auth, "/curator/exhibitions", "POST", input),
    onSuccess: () => client.invalidateQueries({ queryKey: ["curator-exhibitions"] })
  });
  if (query.isLoading)
    return (
      <PageFrame>
        <LoadingState label="Loading exhibition drafts" />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame>
        <ErrorState retry={() => void query.refetch()}>
          Exhibition drafts are unavailable.
        </ErrorState>
      </PageFrame>
    );
  const createDraft = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    create.mutate(
      {
        title: text(data, "title"),
        purpose: text(data, "purpose"),
        curatorNotes: text(data, "curatorNotes")
      },
      { onSuccess: () => form.reset() }
    );
  };
  return (
    <PageFrame>
      <Seo
        title="Exhibition Drafts | EAF Curator"
        description="Private EAF exhibition planning workspace."
      />
      <Link to="/curator">← Curator home</Link>
      <div className="mt-4 mb-5">
        <p className="eyebrow">Private working space</p>
        <h1>Exhibition Drafts</h1>
        <p>Bring artworks together around a simple idea. Nothing here is published.</p>
      </div>
      <Card className="curator-card mb-5">
        <Card.Body>
          <Card.Title>Start an Exhibition Idea</Card.Title>
          <Form onSubmit={createDraft}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="new-exhibition-title">
                  <Form.Label>Working title</Form.Label>
                  <Form.Control name="title" required maxLength={180} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="new-exhibition-purpose">
                  <Form.Label>What is the main idea? (optional)</Form.Label>
                  <Form.Control name="purpose" maxLength={4000} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="new-exhibition-notes">
                  <Form.Label>Notes (optional)</Form.Label>
                  <Form.Control as="textarea" rows={2} name="curatorNotes" maxLength={10000} />
                </Form.Group>
              </Col>
            </Row>
            <Button className="mt-3" type="submit" disabled={create.isPending}>
              Create draft
            </Button>
          </Form>
          {create.isError ? (
            <Alert className="mt-3" variant="danger">
              {errorMessage(create.error)}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>
      {query.data.exhibitions.length ? (
        <div className="exhibition-draft-list">
          {query.data.exhibitions.map((exhibition) => (
            <ExhibitionDraftEditor
              key={exhibition.exhibitionId}
              exhibition={exhibition}
              artworks={query.data.artworks}
            />
          ))}
        </div>
      ) : (
        <EmptyState title="No Exhibition Drafts Yet">
          Start with a working title. Artworks can be added a little at a time.
        </EmptyState>
      )}
    </PageFrame>
  );
}

function ExhibitionDraftEditor({
  exhibition,
  artworks
}: {
  exhibition: ExhibitionWorkspace["exhibitions"][number];
  artworks: ExhibitionWorkspace["artworks"];
}) {
  const auth = useAuth();
  const client = useQueryClient();
  const [artworkIds, setArtworkIds] = useState(exhibition.artworks.map((item) => item.artworkId));
  useEffect(() => {
    setArtworkIds(exhibition.artworks.map((item) => item.artworkId));
  }, [exhibition.artworks]);
  const update = useMutation({
    mutationFn: (input: {
      title: string;
      purpose: string;
      curatorNotes: string;
      artworkIds: string[];
    }) =>
      commandRequest(auth, `/curator/exhibitions/${exhibition.exhibitionId}`, "PUT", {
        expectedVersion: exhibition.version,
        ...input
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["curator-exhibitions"] });
      void client.invalidateQueries({ queryKey: ["curator-artwork"] });
    }
  });
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    update.mutate({
      title: text(data, "title"),
      purpose: text(data, "purpose"),
      curatorNotes: text(data, "curatorNotes"),
      artworkIds
    });
  };
  const selected = artworkIds
    .map((id) => artworks.find((artwork) => artwork.artworkId === id))
    .filter((artwork): artwork is ExhibitionWorkspace["artworks"][number] => Boolean(artwork));
  const available = artworks.filter((artwork) => !artworkIds.includes(artwork.artworkId));
  const move = (index: number, offset: number) => {
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= artworkIds.length) return;
    setArtworkIds((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (item) next.splice(nextIndex, 0, item);
      return next;
    });
  };
  return (
    <Card className="curator-card">
      <Card.Body>
        <Form onSubmit={save}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId={`exhibition-title-${exhibition.exhibitionId}`}>
                <Form.Label>Working title</Form.Label>
                <Form.Control name="title" defaultValue={exhibition.title} required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId={`exhibition-purpose-${exhibition.exhibitionId}`}>
                <Form.Label>Main idea</Form.Label>
                <Form.Control name="purpose" defaultValue={exhibition.purpose} />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId={`exhibition-notes-${exhibition.exhibitionId}`}>
                <Form.Label>Curator notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="curatorNotes"
                  defaultValue={exhibition.curatorNotes}
                />
              </Form.Group>
            </Col>
          </Row>
          <h2 className="h3 mt-4">Artworks</h2>
          {selected.length ? (
            <ol className="exhibition-artwork-order">
              {selected.map((artwork, index) => (
                <li key={artwork.artworkId}>
                  <span>
                    {artwork.title || "Untitled"} · Sequence{" "}
                    {artwork.photographicSequence ?? "unknown"}
                  </span>
                  <div className="d-flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      type="button"
                      variant="outline-light"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      Move up
                    </Button>
                    <Button
                      size="sm"
                      type="button"
                      variant="outline-light"
                      disabled={index === selected.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      Move down
                    </Button>
                    <Button
                      size="sm"
                      type="button"
                      variant="outline-danger"
                      onClick={() =>
                        setArtworkIds((current) => current.filter((id) => id !== artwork.artworkId))
                      }
                    >
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p>No artworks added yet.</p>
          )}
          <Form.Group controlId={`exhibition-add-${exhibition.exhibitionId}`}>
            <Form.Label>Add an artwork</Form.Label>
            <Form.Select
              value=""
              onChange={(event) => {
                const id = event.currentTarget.value;
                if (id) setArtworkIds((current) => [...current, id]);
              }}
            >
              <option value="">Choose an artwork…</option>
              {available.map((artwork) => (
                <option key={artwork.artworkId} value={artwork.artworkId}>
                  {artwork.photographicSequence ?? "—"} · {artwork.title || "Untitled"}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Button className="mt-3" type="submit" disabled={update.isPending}>
            Save exhibition draft
          </Button>
          {update.isError ? (
            <Alert className="mt-3" variant="danger">
              {errorMessage(update.error)}
            </Alert>
          ) : null}
        </Form>
      </Card.Body>
    </Card>
  );
}

export function CuratorGovernancePage() {
  const auth = useAuth();
  const query = useQuery({
    queryKey: ["curator-governance"],
    queryFn: () =>
      auth
        .authorizedFetch<unknown>("/curator/governance")
        .then((value) => curatorGovernanceSchema.parse(value))
  });
  if (query.isLoading)
    return (
      <PageFrame>
        <LoadingState label="Loading governance records" />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame>
        <ErrorState retry={() => void query.refetch()}>
          Governance records are unavailable.
        </ErrorState>
      </PageFrame>
    );
  return (
    <PageFrame>
      <Seo
        title="AI Governance | EAF Curator"
        description="EAF AI method and vocabulary governance."
      />
      <Link to="/curator">← Work queue</Link>
      <div className="mt-4 mb-5">
        <p className="eyebrow">Governed change</p>
        <h1>AI and Vocabulary Governance</h1>
        <p>
          Methods, models, prices, evaluations, and vocabulary proposals are versioned evidence.
          Nothing is promoted automatically.
        </p>
      </div>
      <ProgressiveSection title="AI Methods" initiallyOpen>
        <ResponsiveTable
          caption="AI methods"
          headers={["Method", "Version", "Status", "Created"]}
          rows={query.data.methods.map((method) => [
            method.methodKey,
            method.semanticVersion,
            formatMachineLabel(method.status),
            formatDate(method.createdAt)
          ])}
        />
      </ProgressiveSection>
      <ProgressiveSection title="Model and Price Snapshots">
        <ResponsiveTable
          caption="Model and price snapshots"
          headers={["Profile", "Model", "Status", "Input / 1M", "Output / 1M"]}
          rows={query.data.models.map((model) => [
            model.profileKey,
            model.modelIdentifier,
            formatMachineLabel(model.status),
            model.inputPerMillion === null ? "—" : `$${model.inputPerMillion}`,
            model.outputPerMillion === null ? "—" : `$${model.outputPerMillion}`
          ])}
        />
      </ProgressiveSection>
      <ProgressiveSection title="Evaluation Runs">
        {query.data.evaluations.length ? (
          <ResponsiveTable
            caption="Evaluation runs"
            headers={["Run", "State", "Results", "Passed", "Created"]}
            rows={query.data.evaluations.map((run) => [
              run.evaluationRunId,
              formatMachineLabel(run.state),
              run.resultCount,
              run.passedCount,
              formatDate(run.createdAt)
            ])}
          />
        ) : (
          <p>No evaluation run has been recorded.</p>
        )}
      </ProgressiveSection>
      <ProgressiveSection title="Vocabulary Proposals">
        {query.data.vocabularyProposals.length ? (
          <ResponsiveTable
            caption="Vocabulary proposals"
            headers={["Scheme", "Label", "State", "Proposed"]}
            rows={query.data.vocabularyProposals.map((proposal) => [
              proposal.schemeKey,
              proposal.proposedLabel,
              formatMachineLabel(proposal.state),
              formatDate(proposal.proposedAt)
            ])}
          />
        ) : (
          <p>No vocabulary proposals are waiting.</p>
        )}
      </ProgressiveSection>
    </PageFrame>
  );
}

function CuratorHeading({ children }: { children?: ReactNode }) {
  const auth = useAuth();
  return (
    <div className="curator-heading">
      <div>
        <p className="eyebrow">Protected workspace</p>
        <h1>Curator Work Queue</h1>
        <p>Signed in as {auth.currentUser?.displayName}</p>
      </div>
      <div className="d-flex gap-2">
        {children}
        <Button variant="outline-light" onClick={() => void auth.logout()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <Card className="curator-card">
      <Card.Body>
        <strong className="curator-metric__value">{value}</strong>
        <span>{label}</span>
      </Card.Body>
    </Card>
  );
}

async function uploadArtworkFile(
  auth: ReturnType<typeof useAuth>,
  artworkId: string,
  file: File,
  setProgress: (value: number) => void
) {
  setProgress(30);
  const checksum = await sha256Base64(file);
  const checksumMd5 = await md5Base64(file);
  const intent = uploadIntentSchema.parse(
    await commandRequest(auth, `/curator/artworks/${artworkId}/upload-intents`, "POST", {
      originalFilename: file.name,
      mediaType: file.type,
      maximumBytes: file.size,
      checksumSha256Base64: checksum,
      checksumMd5Base64: checksumMd5,
      side: "front"
    })
  );
  let putSucceeded = false;
  try {
    setProgress(50);
    const upload = await fetch(intent.uploadUrl, {
      method: "PUT",
      headers: intent.requiredHeaders,
      body: file
    });
    if (!upload.ok)
      throw new Error(`Private image storage rejected the upload with status ${upload.status}.`);
    putSucceeded = true;
    setProgress(80);
    await commandRequest(
      auth,
      `/curator/upload-intents/${intent.uploadIntentId}/finalize`,
      "POST",
      {}
    );
    setProgress(100);
    return { uploadIntentId: intent.uploadIntentId, assetId: intent.assetId };
  } catch (caught) {
    if (putSucceeded)
      throw new Error(
        "The image reached private storage, but finalization did not complete. Keep this draft and ask an administrator to retry finalization before uploading again.",
        { cause: caught }
      );
    const cancelled = await commandRequest(
      auth,
      `/curator/upload-intents/${intent.uploadIntentId}/cancel`,
      "POST",
      {}
    )
      .then(() => true)
      .catch(() => false);
    const detail =
      caught instanceof Error && caught.message !== "Failed to fetch"
        ? ` ${caught.message}`
        : " The browser could not reach private image storage.";
    throw new Error(
      `The draft was saved, but its image upload did not finish.${detail} ${
        cancelled
          ? "The empty reservation was cancelled; retry from the saved draft."
          : "The reservation could not be cancelled automatically; do not upload again until it is reviewed."
      }`,
      { cause: caught }
    );
  }
}

async function commandRequest(
  auth: ReturnType<typeof useAuth>,
  path: string,
  method: "POST" | "PATCH" | "PUT",
  body: unknown
) {
  return auth.authorizedFetch<unknown>(path, {
    method,
    headers: { "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify(body)
  });
}

function leadingSequence(filename: string): number | null {
  const match = filename.match(/^\s*#?\s*(\d+)/);
  if (!match?.[1]) return null;
  const value = Number(match[1]);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

async function sha256Base64(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  let binary = "";
  for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

async function md5Base64(file: File): Promise<string> {
  return btoa(SparkMD5.ArrayBuffer.hash(await file.arrayBuffer(), true));
}

function stringValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return typeof value === "number" ? String(value) : JSON.stringify(value);
}

function text(data: FormData, key: string): string {
  const value = data.get(key);
  return typeof value === "string" ? value : "";
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The action could not be completed.";
}
